import type { Product, SizeVariant } from '@/types'
import { firstInStockProductSize } from '@/lib/productSize'

export function getSizeVariant(
  product: Product,
  size: string
): SizeVariant | undefined {
  return product.sizes?.find((v) => v.size === size)
}

/** All variants in stored order. */
export function productVariants(product: Product): SizeVariant[] {
  return product.sizes ?? []
}

export function variantPriceInfo(variant?: SizeVariant, product?: Product) {
  const price = variant?.discountPrice ?? variant?.price ?? product?.price ?? 0
  const originalPrice = variant?.price ?? product?.price ?? 0
  return {
    price,
    originalPrice,
    hasDiscount: Boolean(variant?.discountPrice),
    stock: variant?.quantity ?? product?.quantity ?? 0,
  }
}

export function variantImage(
  variant: SizeVariant | undefined,
  product: Product
): string | undefined {
  return variant?.imageUrl || product.images[0]
}

export function discountPercent(price: number, originalPrice: number): number {
  if (originalPrice <= 0 || price >= originalPrice) return 0
  return Math.round((1 - price / originalPrice) * 100)
}

/** Sell price for the chosen variant (used for shop sort). */
export function selectedVariantSortPrice(
  product: Product,
  selectedSize?: string
): number {
  if (product.sizes?.length) {
    const size =
      selectedSize && getSizeVariant(product, selectedSize)
        ? selectedSize
        : firstInStockProductSize(product)
    const variant = getSizeVariant(product, size)
    if (variant) return variant.discountPrice ?? variant.price
  }
  return product.discountPrice ?? product.price
}

/** Lowest or highest sell price across variants (fallback when no selection). */
export function productSortPrice(
  product: Product,
  direction: 'asc' | 'desc' = 'asc'
): number {
  const prices =
    product.sizes?.length
      ? product.sizes.map((variant) => variant.discountPrice ?? variant.price)
      : [product.discountPrice ?? product.price]

  if (prices.length === 0) return 0
  return direction === 'asc' ? Math.min(...prices) : Math.max(...prices)
}
